import React, { useState, useEffect } from 'react'
import { Button, Card, Col, Form, Input, Radio, Row, Table, Typography, message } from 'antd'
import adminService from '../../../services/admin'

const { Title, Text } = Typography
const { TextArea } = Input

const AdminEmail = () => {
  const [form] = Form.useForm()
  const [sending, setSending] = useState(false)
  const [recipients, setRecipients] = useState([])
  const [loadingRecipients, setLoadingRecipients] = useState(false)
  const [target, setTarget] = useState('subscribers')
  const [sampleSize, setSampleSize] = useState(10)

  const fetchRecipients = () => {
    const t = form.getFieldValue('target') || target
    const s = form.getFieldValue('sampleSize') || sampleSize
    if (!t) return
    const params = { target: t }
    if (t === 'random') params.sampleSize = Math.max(1, Math.min(500, Number(s) || 10))
    setLoadingRecipients(true)
    adminService
      .getEmailRecipients(params)
      .then((data) => setRecipients(data.recipients || []))
      .catch(() => setRecipients([]))
      .finally(() => setLoadingRecipients(false))
  }

  useEffect(() => {
    fetchRecipients()
  }, [])

  const onValuesChange = (changed, all) => {
    if ('target' in changed) setTarget(changed.target)
    if ('sampleSize' in changed) setSampleSize(changed.sampleSize)
    if (changed.target !== undefined || changed.sampleSize !== undefined) {
      setTimeout(fetchRecipients, 0)
    }
  }

  const onFinish = async (values) => {
    try {
      setSending(true)
      await adminService.broadcastEmail(values)
      message.success('Broadcast email sent (or queued) successfully')
      form.resetFields()
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
          'Failed to send email. Ensure SendGrid is configured on the server.'
      )
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Email Broadcast
          </Title>
          <Text type="secondary">
            Compose Gmail-style updates and send them to all users or newsletter subscribers.
          </Text>
        </Col>
      </Row>

      <Card
        style={{
          maxWidth: 960,
          margin: '0 auto',
          borderRadius: 12,
          boxShadow: '0 10px 25px rgba(15, 23, 42, 0.12)'
        }}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          onValuesChange={onValuesChange}
          initialValues={{ target: 'subscribers', sampleSize: 10 }}
        >
          <Form.Item label="Recipients" name="target">
            <Radio.Group>
              <Radio.Button value="all">All users</Radio.Button>
              <Radio.Button value="subscribers">Newsletter subscribers</Radio.Button>
              <Radio.Button value="random">Random sample</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) =>
              getFieldValue('target') === 'random' ? (
                <Form.Item
                  label="Sample size"
                  name="sampleSize"
                  rules={[{ required: true, message: 'Enter how many users to sample' }]}
                >
                  <Input type="number" min={1} max={500} placeholder="e.g. 50" />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            label="Subject"
            name="subject"
            rules={[{ required: true, message: 'Please enter an email subject' }]}
          >
            <Input placeholder="What’s the update about?" />
          </Form.Item>

          <Form.Item
            label="Message"
            name="body"
            rules={[{ required: true, message: 'Please enter the email content' }]}
          >
            <TextArea
              rows={10}
              placeholder={
                'Write your update like an email. You can paste rich text, but it will be sent as plain text.'
              }
              style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={sending}>
              Send email
            </Button>
          </Form.Item>
        </Form>

        <Typography.Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>
          Recipients ({recipients.length})
        </Typography.Title>
        <Table
          size="small"
          loading={loadingRecipients}
          dataSource={recipients}
          rowKey="email"
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `Total ${t}` }}
          columns={[
            { title: 'Email', dataIndex: 'email', key: 'email', ellipsis: true },
            { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true, render: (n) => n || '—' },
            {
              title: 'Source',
              dataIndex: 'source',
              key: 'source',
              width: 100,
              render: (s) => (s === 'guest' ? 'Guest subscriber' : 'User')
            }
          ]}
        />
      </Card>
    </>
  )
}

export default AdminEmail

